/**
 * SOC2 ticket-based WebSocket helpers.
 *
 * Flow:
 * 1. POST /api/auth/ws-ticket (same-origin cookie session) → short-lived ticket (~45s)
 * 2. Open wss://…/ws/{channel}?ticket=… immediately (never reuse ticket)
 * 3. On reconnect: mint a NEW ticket each time
 *
 * Never place long-lived tokens or API keys in the WebSocket URL.
 */

'use client';

// =============================================================================
// CONSTANTS
// =============================================================================

const DEFAULT_WS_BASE = 'wss://api.sandbox.mechagora.net';
const TICKET_MINT_PATH = '/api/auth/ws-ticket';
const DEFAULT_LOGIN_PATH = '/auth/client-login';

/** Channels that require a valid ticket (private user streams). */
const PRIVATE_CHANNELS = new Set([
  'orders',
  'notifications',
  'executions',
  'portfolio',
  'chat',
]);

// =============================================================================
// TYPES
// =============================================================================

export type WsChannel =
  | 'market-data'
  | 'orders'
  | 'notifications'
  | 'executions'
  | 'portfolio'
  | 'chat'
  | (string & {});

export interface MintWsTicketResult {
  ticket: string;
  /** Seconds until expiry when provided by BFF */
  expiresIn?: number;
  channel?: string;
  /** Channels authorized by the ticket (from BFF) */
  channels?: string[];
  /** Edge WS base from mint response (preferred over env when present) */
  wsBaseUrl?: string;
}

export interface MintWsTicketOptions {
  /** Single channel — sent as `channels: [channel]` to the BFF */
  channel?: WsChannel;
  /** Explicit channel list (preferred when requesting multiple) */
  channels?: WsChannel[];
  signal?: AbortSignal;
}

export interface ConnectSecureWsOptions {
  channel: WsChannel;
  protocols?: string | string[];
  /**
   * Require a ticket before opening the socket.
   * Defaults to true for private channels, false for public (e.g. market-data).
   */
  requireTicket?: boolean;
  /**
   * On 401 from mint: soft-fail (throw / continue without redirect).
   * Defaults to true for public channels, false for private.
   */
  softFailAuth?: boolean;
  /** Login path used when auth fails and softFailAuth is false */
  loginPath?: string;
  signal?: AbortSignal;
}

export class WsTicketError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'WsTicketError';
    this.status = status;
    this.code = code;
  }
}

// =============================================================================
// URL HELPERS
// =============================================================================

/**
 * Normalize WS base so we always append `/ws/{channel}`.
 * Strips trailing slash and a trailing `/ws` segment if present.
 */
export function normalizeWsBase(url: string): string {
  let base = (url || DEFAULT_WS_BASE).trim().replace(/\/+$/, '');
  if (base.endsWith('/ws')) {
    base = base.slice(0, -3);
  }
  return base || DEFAULT_WS_BASE;
}

/**
 * Default WS base: NEXT_PUBLIC_WS_BASE_URL || sandbox edge.
 */
export function getWsBaseUrl(): string {
  const raw =
    process.env.NEXT_PUBLIC_WS_BASE_URL ||
    process.env.NEXT_PUBLIC_WS_URL ||
    DEFAULT_WS_BASE;
  return normalizeWsBase(raw);
}

export function isPrivateChannel(channel: string): boolean {
  return PRIVATE_CHANNELS.has(channel);
}

/**
 * Build `wss://…/ws/{channel}` optionally with a short-lived ticket query param.
 * Never attach apiKey / long-lived session tokens.
 *
 * @param baseUrlOverride — when mint returns `ws_base_url`, prefer that over env.
 */
export function buildSecureWsUrl(
  channel: string,
  ticket?: string | null,
  baseUrlOverride?: string | null
): string {
  const base = normalizeWsBase(baseUrlOverride || getWsBaseUrl());
  const cleaned = channel.replace(/^\/+/, '').replace(/^ws\//, '');
  const path = `/ws/${cleaned}`;
  if (ticket) {
    return `${base}${path}?ticket=${encodeURIComponent(ticket)}`;
  }
  return `${base}${path}`;
}

// =============================================================================
// TICKET MINT
// =============================================================================

/**
 * Mint a short-lived WebSocket ticket from the same-origin BFF.
 * Uses credentials: 'include' so verify-client session cookies are sent
 * (same cookie jar as GET /api/auth/verify-client).
 *
 * Body shape matches POST /api/auth/ws-ticket: `{ channels?: string[] }`.
 * CSRF is not required on this route (session cookie + rate limit only).
 *
 * On 401, retries once after a short delay (post-login cookie race / JWKS cold
 * start — same pattern as ClientProtectedRoute → verify-client).
 *
 * Never logs the full ticket value.
 */
export async function mintWsTicket(
  options: MintWsTicketOptions = {}
): Promise<MintWsTicketResult> {
  const { channel, channels, signal } = options;

  // BFF expects `channels` (array). Singular `channel` is normalized into it.
  let requested: string[] | undefined;
  if (channels?.length) {
    requested = channels.map(String);
  } else if (channel) {
    requested = [String(channel)];
  }

  const body = requested?.length
    ? JSON.stringify({ channels: requested })
    : JSON.stringify({});

  const attempt = async (): Promise<Response> =>
    fetch(TICKET_MINT_PATH, {
      method: 'POST',
      // Critical: same-origin cookie session (sessionToken / clientSessionToken)
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body,
      signal,
    });

  let res = await attempt();

  // Brief single retry: cookie may not be visible on first paint after login redirect
  if (res.status === 401) {
    await new Promise((r) => setTimeout(r, 200));
    if (signal?.aborted) {
      throw new WsTicketError(
        'WebSocket ticket mint aborted',
        401,
        'aborted'
      );
    }
    res = await attempt();
  }

  if (res.status === 401) {
    throw new WsTicketError(
      'WebSocket ticket mint unauthorized',
      401,
      'unauthorized'
    );
  }

  if (!res.ok) {
    throw new WsTicketError(
      `WebSocket ticket mint failed (${res.status})`,
      res.status,
      'mint_failed'
    );
  }

  const data = (await res.json()) as {
    ticket?: string;
    token?: string;
    expiresIn?: number;
    expires_in?: number;
    channel?: string;
    channels?: string[];
    ws_base_url?: string;
    wsBaseUrl?: string;
  };

  const ticket = data.ticket ?? data.token;
  if (!ticket || typeof ticket !== 'string') {
    throw new WsTicketError(
      'WebSocket ticket response missing ticket',
      res.status,
      'invalid_response'
    );
  }

  const wsBaseUrl = data.ws_base_url ?? data.wsBaseUrl;
  const expiresIn = data.expiresIn ?? data.expires_in;

  // Dev-only prefix log — never log full ticket (SOC2)
  if (process.env.NODE_ENV === 'development') {
    console.debug('[secureSocket] minted ws ticket', {
      channel: channel ?? data.channel,
      channels: data.channels ?? requested,
      ticketPrefix: `${ticket.slice(0, 4)}…`,
      expiresIn,
      wsBaseUrl: wsBaseUrl ? normalizeWsBase(wsBaseUrl) : undefined,
    });
  }

  return {
    ticket,
    expiresIn,
    channel: data.channel ?? channel,
    channels: data.channels ?? requested,
    wsBaseUrl: wsBaseUrl ? normalizeWsBase(wsBaseUrl) : undefined,
  };
}

/**
 * Handle 401 from ticket mint: optional redirect to login.
 * @returns true if a hard redirect was initiated
 */
export function handleWsTicketUnauthorized(options?: {
  softFail?: boolean;
  loginPath?: string;
}): boolean {
  const softFail = options?.softFail ?? false;
  const loginPath = options?.loginPath ?? DEFAULT_LOGIN_PATH;

  if (softFail || typeof window === 'undefined') {
    return false;
  }

  const returnTo = encodeURIComponent(
    `${window.location.pathname}${window.location.search}`
  );
  window.location.assign(`${loginPath}?returnTo=${returnTo}`);
  return true;
}

// =============================================================================
// CONNECT
// =============================================================================

/**
 * Mint a ticket (when required/available) and open a WebSocket immediately.
 * Call again on every reconnect — tickets must not be reused.
 */
export async function connectSecureWs(
  options: ConnectSecureWsOptions
): Promise<WebSocket> {
  const {
    channel,
    protocols,
    requireTicket = isPrivateChannel(channel),
    softFailAuth = !isPrivateChannel(channel),
    loginPath = DEFAULT_LOGIN_PATH,
    signal,
  } = options;

  let ticket: string | undefined;
  let wsBaseFromMint: string | undefined;

  try {
    const minted = await mintWsTicket({ channel, signal });
    ticket = minted.ticket;
    wsBaseFromMint = minted.wsBaseUrl;
  } catch (err) {
    const isAuth =
      err instanceof WsTicketError && err.status === 401;

    if (isAuth) {
      handleWsTicketUnauthorized({ softFail: softFailAuth, loginPath });
      if (requireTicket) {
        throw err;
      }
      // Public channel: fall through and open without ticket
    } else if (requireTicket) {
      throw err;
    }
    // Public / optional ticket: continue without ticket on soft mint failures
  }

  if (requireTicket && !ticket) {
    throw new WsTicketError(
      'WebSocket ticket required but not obtained',
      401,
      'missing_ticket'
    );
  }

  const url = buildSecureWsUrl(channel, ticket, wsBaseFromMint);
  return protocols !== undefined
    ? new WebSocket(url, protocols)
    : new WebSocket(url);
}
